import React, { useState } from 'react';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { Card } from '../../../components/ui';

/**
 * Clickable areas, coordinates scaled to the 1170 × 1615 image
 * (from the original 2000 × 2400: X × 0.585, Y × 0.673, shifted up 100px).
 *
 * Each area used to carry its own hover colour — green, fuchsia, blue, red,
 * slate — which grouped areas that aren't related. One highlight colour now
 * means "this is the one you're pointing at", which is the only thing the
 * highlight was ever communicating.
 */
export const AIRPORT_AREAS = [
    {
        id: 'concourse-a',
        name: 'Concourse A',
        path: `M 205 1064 L 475 1064 L 475 1132 L 205 1132 Z`,
    },
    {
        id: 'concourse-b',
        name: 'Concourse B',
        path: `M 680 1064 L 950 1064 L 950 1132 L 680 1132 Z`,
    },
    {
        id: 'concourse-d',
        name: 'Concourse D',
        path: `M 155 220 L 300 220 L 400 300 L 480 300 L 480 370 L 410 435 L 320 340 L 280 300 L 155 300 Z`,
    },
    {
        id: 'concourse-e',
        name: 'Concourse E',
        path: `M 1015 220 L 870 220 L 770 300 L 690 300 L 690 370 L 760 435 L 850 340 L 890 300 L 1015 300 Z`,
    },
    {
        id: 'central-concourse',
        name: 'Central Concourse',
        path: `M 395 300 L 765 300 L 760 438 L 410 438 Z`,
        locationAliases: ['central concourse', 'north node', 'dcr_north_node'],
    },
    {
        id: 'north-node',
        name: 'North Node',
        path: `M 410 438 L 530 563 L 553 563 L 627 563 L 630 563 L 760 438 Z`,
        locationAliases: ['north node', 'dcr_north_node'],
    },
    {
        id: 'south-node',
        name: 'Concourse C',
        path: `M 533 977 L 627 977 L 627 563 L 533 563 Z`,
    },
    {
        id: 'terminal',
        name: 'MTB',
        path: `M 470 977 L 680 977 L 695 1078 L 675 1270 L 480 1270 L 460 1078 Z`,
    },
    {
        id: 'mosque',
        name: 'Mosque',
        path: 'M 573 1410 m -54,0 a 54,54 0 1,0 108,0 a 54,54 0 1,0 -108,0',
    },
];

const AREA_LIST_COLUMNS = [
    ['concourse-a', 'concourse-b', 'south-node', 'terminal', 'mosque'],
    ['north-node', 'central-concourse', 'concourse-d', 'concourse-e'],
];

const AirportMap = ({ onAreaSelect }) => {
    const [activeArea, setActiveArea] = useState(null);
    const [labelPos, setLabelPos] = useState({ x: 0, y: 0 });

    return (
        <div className="mx-auto w-full max-w-3xl">
            <div className="rounded-lg border border-border bg-surface p-4 shadow-sm sm:p-6">
                <h2 className="text-center text-lg font-semibold text-foreground sm:text-xl">
                    Hamad International Airport
                </h2>
                <p className="mt-1 text-center text-sm text-muted-fg">
                    Select an area on the map, or from the list below
                </p>

                <div className="relative mt-5 overflow-hidden rounded-lg border border-border">
                    <svg
                        viewBox="0 150 1170 1465"
                        className="mx-auto h-auto w-full max-w-[600px]"
                        preserveAspectRatio="xMidYMid meet"
                        role="img"
                        aria-label="Airport terminal map"
                    >
                        {AIRPORT_AREAS.map((area) => {
                            const isActive = activeArea === area.id;
                            return (
                                <g
                                    key={area.id}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={area.name}
                                    onMouseEnter={() => setActiveArea(area.id)}
                                    onMouseLeave={() => setActiveArea(null)}
                                    onFocus={() => setActiveArea(area.id)}
                                    onBlur={() => setActiveArea(null)}
                                    onClick={() => onAreaSelect(area)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onAreaSelect(area);
                                        }
                                    }}
                                    onMouseMove={(e) => {
                                        const svg = e.currentTarget.ownerSVGElement;
                                        const rect = svg.getBoundingClientRect();
                                        setLabelPos({
                                            x: e.clientX - rect.left + 10,
                                            y: e.clientY - rect.top + 10,
                                        });
                                    }}
                                    className="cursor-pointer"
                                >
                                    {/* Native tooltip so the name is reachable without hover styling. */}
                                    <title>{area.name}</title>
                                    <path
                                        d={area.path}
                                        fill={isActive ? 'rgb(var(--color-accent))' : 'rgb(var(--color-muted))'}
                                        stroke={
                                            isActive
                                                ? 'rgb(var(--color-accent-hover))'
                                                : 'rgb(var(--color-border-strong))'
                                        }
                                        strokeWidth="2"
                                        className="transition-all duration-base ease-out"
                                    />
                                </g>
                            );
                        })}
                    </svg>

                    {activeArea && (
                        <div
                            className="pointer-events-none absolute z-10 rounded-md border border-border bg-surface/95 px-2.5 py-1 text-sm text-foreground shadow-md backdrop-blur"
                            style={{ left: `${labelPos.x}px`, top: `${labelPos.y}px` }}
                        >
                            {AIRPORT_AREAS.find((a) => a.id === activeArea)?.name}
                        </div>
                    )}
                </div>
            </div>

            {/* The map is hover-driven, which does not exist on a touch screen —
                this list is the equivalent path for phones and for keyboards. */}
            <div className="mt-5">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-fg">
                    All areas
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {AREA_LIST_COLUMNS.map((column, columnIndex) => (
                        <div key={columnIndex} className="space-y-3">
                            {column.map((areaId) => {
                                const area = AIRPORT_AREAS.find((item) => item.id === areaId);
                                if (!area) return null;

                                return (
                                    <Card
                                        key={area.id}
                                        interactive
                                        onClick={() => onAreaSelect(area)}
                                        onMouseEnter={() => setActiveArea(area.id)}
                                        onMouseLeave={() => setActiveArea(null)}
                                        className="group"
                                    >
                                        <div className="flex min-h-11 items-center gap-3 px-4 py-3">
                                            <span className="min-w-0 flex-1 text-sm font-medium text-foreground">
                                                {area.name}
                                            </span>
                                            <ChevronRight
                                                style={{ fontSize: 20 }}
                                                aria-hidden="true"
                                                className="shrink-0 text-muted-fg transition-transform duration-base group-hover:translate-x-1 group-hover:text-accent"
                                            />
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AirportMap;
